import { useMutation } from '@apollo/client';
import type { UseSubmitInventoryList } from '@openx/components-administration/lib/utils/context/InventoryListHookTypes.ts';
import { difference } from '@openx/components-form/difference.ts';
import { FormTypes, type InventoryListFormState, ListTypesAPI } from '@openx/types/inventoryList.ts';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CreateInventoryListDocument } from '../../../../graphql/mutation/inventoryList/createInventoryList.generated.tsx';
import { UpdateInventorylistDocument } from '../../../../graphql/mutation/inventoryList/updateInventoryList.generated.tsx';

export const useSubmitInventoryList: UseSubmitInventoryList = (id?: string) => {
  const [createInventoryList, { loading: createLoading }] = useMutation(CreateInventoryListDocument);
  const [updateInventoryList, { loading: updateLoading }] = useMutation(UpdateInventorylistDocument);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const submitForm = useCallback(
    async (formValues: InventoryListFormState, initValues: InventoryListFormState) => {
      if (id) {
        const diff = difference(formValues, initValues);
        const payload = [
          {
            ...diff,
            id,
          },
        ];

        await updateInventoryList({
          variables: {
            payload,
          },
          refetchQueries: ['GetInventoryListsList', 'GetInventoryListById'],
          onCompleted: data => {
            enqueueSnackbar(t('Inventory List has been updated.'), { variant: 'success' });
            navigate(`/inventory-lists/${data.updateInventorylist[0].id}/details`);
          },
        });
        return;
      }

      const payload = {
        ...formValues,
        type_full: formValues.type_full === FormTypes.BUNDLES ? ListTypesAPI.BUNDLE : ListTypesAPI.DOMAIN,
      };

      await createInventoryList({
        variables: {
          payload,
        },
        refetchQueries: ['GetInventoryListsList'],
        onCompleted(data) {
          enqueueSnackbar(t('Inventory List has been created.'), { variant: 'success' });
          navigate(`/inventory-lists/${data.createInventorylist[0].id}/details`);
        },
      });
    },
    [createInventoryList, updateInventoryList, id, navigate, enqueueSnackbar, t]
  );

  return { submitForm, loading: createLoading || updateLoading };
};
